import { Injectable, OnDestroy } from '@angular/core';
import {
  BehaviorSubject,
  defer,
  EMPTY,
  forkJoin,
  Observable,
  of,
  Subject,
  Subscription,
  throwError,
} from 'rxjs';
import {
  catchError,
  expand,
  filter,
  map,
  reduce,
  shareReplay,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { ApiEndpointsService } from 'src/app/core/services/api-endpoints.service';
import { ApiHttpService } from 'src/app/core/services/api-http.service';
import { AuthService } from 'src/app/core/services/auth/auth.service';
import { TeamMember, TeamRole } from '../team/team-member.model';
import { ConfigService } from 'src/app/initializer/config.service';
import {
  ITaskApi,
  ITaskComment,
  ITaskDetailData,
  ITopicListItem,
  TaskSource,
} from './task-management.models';
import { AceFundDirectoryItem } from 'src/app/core/models/ace-fund-directory.model';
import { resolveAvatarUrl } from 'src/app/shared/utils/avatar-url';

export interface ITaskDetail {
  taskId?: string;
  title?: string;
  timeStamp?: string | null;
  topic?: string;
  status?: string;
  vehicleId?: string;
  gpId?: string;
  isSuggest: boolean;
  isDialog?: boolean;
  topicList?: ITopicListItem[];
  assigneeList?: TeamMember[];
}

interface AceTaskAccountDto {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  profileImageUrl?: string | null;
  roles?: string[];
}

interface AceTaskCompanyDto {
  id: string;
  name: string;
  code?: string | null;
}

interface AceTaskDto {
  id: string;
  title: string;
  status: string;
  priority: string;
  source?: TaskSource;
  externalSource?: string | null;
  externalId?: string | null;
  externalSyncStatus?: string | null;
  externalSyncError?: string | null;
  externalUrl?: string | null;
  clickUp?: {
    mappingId?: string | null;
    taskId?: string | null;
    url?: string | null;
    syncStatus?: string | null;
    syncError?: string | null;
    checksum?: string | null;
    sourceUpdatedAt?: string | null;
    createdAt?: string | null;
    updatedAt?: string | null;
    pending?: boolean;
  } | null;
  dueAt: string | null;
  dueLabel?: string | null;
  documentsCount?: number;
  commentsCount?: number;
  updatedAt: string;
  createdAt?: string;
  completedAt?: string | null;
  folderId?: string | null;
  notifyInApp?: boolean;
  notifyEmail?: boolean;
  company: AceTaskCompanyDto | null;
  assignees: AceTaskAccountDto[];
  description?: string | null;
  documents?: Array<{
    id: string;
    title: string;
    currentVersion?: {
      originalName?: string | null;
      downloadUrl?: string | null;
    } | null;
  }>;
  comments?: Array<{
    id: string;
    body: string;
    commentText?: string | null;
    author?: AceTaskAccountDto | null;
    externalSource?: string | null;
    externalId?: string | null;
    externalSyncStatus?: string | null;
    externalSyncError?: string | null;
    sourceUpdatedAt?: string | null;
    createdAt: string;
    updatedAt: string;
  }>;
  checklistItems?: Array<{
    id?: string;
    title?: string | null;
    name?: string | null;
    text?: string | null;
    label?: string | null;
    completed?: boolean | null;
    done?: boolean | null;
    status?: string | null;
  }>;
  activity?: Array<{
    id: string;
    type?: string | null;
    message: string;
    metadata?: unknown;
    createdAt: string;
    user?: AceTaskAccountDto | null;
  }>;
}

interface AceTaskListData {
  items: AceTaskDto[];
  nextCursor: string | null;
}

export interface TaskBackendFilters {
  search?: string;
  fundId?: string;
  tab?: string;
  source?: TaskSource;
  priority?: string[];
  status?: string[];
  scope?: string;
  mine?: boolean;
  memberIds?: string[];
  dueFrom?: string;
  dueTo?: string;
  includePastDue?: boolean;
  atRisk?: boolean;
}

interface AceFundDetailDto extends AceFundDirectoryItem {
  name?: string;
  code?: string | null;
}

interface AceFundListData {
  items: AceFundDetailDto[];
  nextCursor?: string | null;
}

interface BaseDataVehicle {
  id: string;
  name?: string;
  code?: string;
  shortName?: string;
  displayName?: string;
  aceFundId?: string;
  fundId?: string;
}

interface BaseDataGp {
  id?: string;
  vehicles?: BaseDataVehicle[];
}

interface CreateAceTaskPayload {
  source?: TaskSource;
  title: string;
  description?: string;
  fundId?: string;
  folderId?: string;
  priority: 'LOW' | 'MEDIUM' | 'HIGH';
  dueAt?: string;
  assigneeIds?: string[];
  participantIds?: string[];
  checklistItems?: Array<{
    id?: string;
    title: string;
    completed: boolean;
    status?: string;
    orderIndex?: number;
  }>;
  notifyInApp: boolean;
  notifyEmail: boolean;
}

interface UpdateAceTaskPayload {
  title?: string;
  description?: string;
  status?: 'TODO' | 'IN_PROGRESS' | 'BLOCKED' | 'IN_REVIEW' | 'DONE';
  priority?: 'LOW' | 'MEDIUM' | 'HIGH';
  dueAt?: string | null;
  checklistItems?: Array<{
    id?: string;
    title: string;
    completed: boolean;
    status?: string;
    orderIndex?: number;
  }>;
  notifyInApp?: boolean;
  notifyEmail?: boolean;
}

interface PresignedUploadResponse {
  uploadUrl: string;
  method: 'PUT';
  headers?: Record<string, string>;
  bucket: string;
  objectKey: string;
}

export interface TaskDocumentUploadPayload {
  title: string;
  objectKey: string;
  bucket: string;
  originalName: string;
  mimeType: string;
  sizeBytes: number;
  category?: string;
  checksum?: string;
}

export interface LegacyTaskDto {
  id: string;
  name: string;
  description: string;
  status: string;
  source?: TaskSource;
  dueAt: string | null;
  dueLabel: string | null;
  topicId: string;
  priority: string;
  documentsCount: number;
  commentsCount: number;
  assignee: TeamMember | null;
  assignees: TeamMember[];
}

export interface DashboardTaskData {
  tasks: ITaskApi[];
  calendarTasks: ITaskApi[];
  topics: ITopicListItem[];
  activeTaskCountByMember: Record<string, number>;
}

export interface TaskListItemUpdatedEvent {
  task: ITaskApi;
}

@Injectable({
  providedIn: 'root',
})
export class ManagementService implements OnDestroy {
  public openDrawerFlag: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  private taskDetails: BehaviorSubject<ITaskDetail | null> =
    new BehaviorSubject<ITaskDetail | null>(null);
  taskDetailsObservable$ = this.taskDetails.asObservable();
  private taskCreated: Subject<void> = new Subject<void>();
  taskCreatedObservable$ = this.taskCreated.asObservable();
  private taskListItemUpdated = new Subject<TaskListItemUpdatedEvent>();
  taskListItemUpdatedObservable$ = this.taskListItemUpdated.asObservable();
  private aceFundsCache?: {
    contextVersion: number;
    observable: Observable<AceFundDetailDto[]>;
  };
  private aceFundDirectoryRef?: readonly AceFundDirectoryItem[];
  private readonly authSessionSubscription: Subscription;

  constructor(
    private apiHttpService: ApiHttpService,
    private apiEndpointsService: ApiEndpointsService,
    private configService: ConfigService,
    private authService: AuthService
  ) {
    this.authSessionSubscription = this.authService.sessionChanges$.subscribe(
      () => {
        this.aceFundsCache = undefined;
        this.aceFundDirectoryRef = undefined;
      }
    );
  }

  ngOnDestroy(): void {
    this.authSessionSubscription.unsubscribe();
  }

  public openDrawer(): void {
    this.openDrawerFlag.next(true);
  }

  public closeDrawer(): void {
    this.openDrawerFlag.value && this.openDrawerFlag.next(false);
  }

  public setTaskDetails(data: ITaskDetail | null): void {
    !this.openDrawerFlag.value && this.taskDetails.next(data);
  }

  getAllTasks(
    vehicleId: string,
    filters: TaskBackendFilters = {},
    gpId?: string
  ) {
    return this.resolveAceFund(vehicleId, gpId).pipe(
      switchMap(fund => {
        if (!fund) {
          return this.raise({ code: 'FUND_NOT_LINKED' });
        }
        return this.getAllAceTasks(filters.fundId || fund.id, filters).pipe(
          map(tasks => this.toLegacyTasksResponse(tasks, fund))
        );
      })
    );
  }

  getDashboardTasks(
    vehicleId: string,
    filters: TaskBackendFilters = {},
    gpId?: string
  ): Observable<DashboardTaskData> {
    return this.resolveAceFund(vehicleId, gpId).pipe(
      switchMap(fund => {
        if (!fund) {
          return this.raise({ code: 'FUND_NOT_LINKED' });
        }

        return this.getAllAceTasks(filters.fundId || fund.id, {
          ...filters,
          status: undefined,
          includePastDue: undefined,
        }).pipe(
          map(tasks => {
            const calendarTasks = this.filterTasksForCalendar(tasks, filters);
            const legacyTasks = tasks.map(task => this.toLegacyTask(task));
            const legacyCalendarTasks = calendarTasks.map(task =>
              this.toLegacyTask(task)
            );
            const activeTaskCountByMember: Record<string, number> = {};

            for (const task of tasks) {
              if (this.toLegacyStatus(task.status) === 'Completed') {
                continue;
              }
              const assignees = task.assignees?.length ? task.assignees : [];
              for (const assignee of assignees) {
                activeTaskCountByMember[assignee.id] =
                  (activeTaskCountByMember[assignee.id] ?? 0) + 1;
              }
            }

            return {
              tasks: legacyTasks,
              calendarTasks: legacyCalendarTasks,
              topics: this.toTopics(calendarTasks, fund),
              activeTaskCountByMember,
            };
          })
        );
      })
    );
  }

  getTaskDetail(_vehicleId: string, taskId: string) {
    return this.apiHttpService
      .get(this.apiEndpointsService.getTask(taskId))
      .pipe(
        map(response =>
          this.toLegacyTaskDetailResponse(
            this.unwrapAceResponse<AceTaskDto>(response)
          )
        )
      );
  }

  submitSuggestTask(
    vehicleId: string,
    data: FormData,
    gpId?: string,
    files: File[] = []
  ) {
    return this.resolveAceFund(vehicleId, gpId).pipe(
      switchMap(fund => {
        if (!fund) {
          return this.raise({ code: 'FUND_NOT_LINKED' });
        }
        return this.apiHttpService.post(
          this.apiEndpointsService.createTask(),
          this.toCreateTaskPayload(vehicleId, data, fund.id)
        );
      }),
      switchMap(response => {
        const taskId = this.extractTaskId(response);
        if (!files.length) {
          return of(response);
        }
        if (!taskId) {
          return this.raise({
            code: 'TASK_ID_MISSING_FOR_UPLOAD',
            message: 'The task was created but its attachment could not be saved.',
          });
        }
        return this.uploadTaskDocuments(taskId, files).pipe(
          map(() => response)
        );
      }),
      tap(() => {
        if (!files.length) {
          this.taskCreated.next();
        }
      })
    );
  }

  updateTask(taskId: string, data: FormData, options: { silent?: boolean } = {}) {
    const updatePayload = this.toUpdateTaskPayload(data);
    const requests: Observable<unknown>[] = [];
    if (Object.keys(updatePayload).length) {
      requests.push(
        this.apiHttpService.patch(
          this.apiEndpointsService.updateTask(taskId),
          updatePayload
        )
      );
    }
    const assigneeIds = this.formArrayValue(data, 'assigneeIds');
    if (data.has('assigneeIds')) {
      requests.push(
        this.apiHttpService.post(this.apiEndpointsService.assignTask(taskId), {
          assigneeIds,
        })
      );
    }

    return (requests.length ? forkJoin(requests) : of([])).pipe(
      tap(() => {
        // Checklist/notification-only autosaves don't affect anything shown in the
        // task list or calendar widgets, so skip the list-wide refresh they trigger.
        if (!options.silent) {
          this.taskCreated.next();
        }
      })
    );
  }

  publishTaskListItemUpdate(taskId: string, detail: ITaskDetailData): void {
    this.taskListItemUpdated.next({
      task: this.toTaskListItem(taskId, detail),
    });
  }

  updateTaskStatus(taskId: string, status: string) {
    return this.apiHttpService.patch(
      this.apiEndpointsService.updateTask(taskId),
      {
        status: this.toAceStatus(status),
      }
    );
  }

  createTaskComment(taskId: string, commentText: string) {
    return this.apiHttpService
      .post(this.apiEndpointsService.createTaskComment(taskId), { commentText })
      .pipe(
        map(response => this.unwrapAceResponse<ITaskComment>(response)),
        tap(() => this.taskCreated.next())
      );
  }

  deleteTask(taskId: string) {
    return this.apiHttpService
      .delete(this.apiEndpointsService.deleteTask(taskId))
      .pipe(tap(() => this.taskCreated.next()));
  }

  uploadTaskDocuments(taskId: string, files: File[]) {
    const uploads = files.map(file => this.uploadTaskDocumentBytes(file));
    return forkJoin(uploads).pipe(
      switchMap(filesToAdd =>
        this.apiHttpService.put(
          this.apiEndpointsService.updateTaskFiles(taskId),
          {
            filesToAdd,
          }
        )
      ),
      tap(() => this.taskCreated.next())
    );
  }

  removeTaskDocument(taskId: string, documentId: string | number) {
    return this.apiHttpService
      .put(this.apiEndpointsService.updateTaskFiles(taskId), {
        filesIdsToRemove: [documentId],
      })
      .pipe(tap(() => this.taskCreated.next()));
  }

  retryClickUpTaskSync(taskId: string) {
    return this.apiHttpService
      .post(this.apiEndpointsService.retryClickUpTaskSync(taskId), {})
      .pipe(tap(() => this.taskCreated.next()));
  }

  refreshClickUpTask(taskId: string) {
    return this.apiHttpService
      .post(this.apiEndpointsService.refreshClickUpTask(taskId), {})
      .pipe(tap(() => this.taskCreated.next()));
  }

  private uploadTaskDocumentBytes(
    file: File
  ): Observable<TaskDocumentUploadPayload> {
    const mimeType = file.type || 'application/octet-stream';
    return this.apiHttpService
      .post(this.apiEndpointsService.presignUpload(), {
        scope: 'document',
        category: 'task',
        fileName: file.name,
        mimeType,
        sizeBytes: file.size,
      })
      .pipe(
        switchMap(response => {
          const presign =
            this.unwrapAceResponse<PresignedUploadResponse>(response);
          return this.apiHttpService
            .put(presign.uploadUrl, file, {
              headers: presign.headers ?? { 'content-type': mimeType },
              responseType: 'text',
            })
            .pipe(
              map(() => ({
                title: file.name,
                objectKey: presign.objectKey,
                bucket: presign.bucket,
                originalName: file.name,
                mimeType,
                sizeBytes: file.size,
                category: 'task',
              }))
            );
        })
      );
  }

  private unwrapAceResponse<T>(response: unknown): T {
    return ((response as { data?: T })?.data ?? response) as T;
  }

  resolveAceFund(
    vehicleId: string,
    gpId?: string,
    options: { allowLegacyNameMatch?: boolean } = {}
  ): Observable<AceFundDetailDto | undefined> {
    return this.getAllAceFunds().pipe(
      map(funds =>
        this.findAceFundForVehicle(
          vehicleId,
          funds,
          gpId,
          options.allowLegacyNameMatch !== false
        )
      )
    );
  }

  private getAllAceFunds(): Observable<AceFundDetailDto[]> {
    const contextVersion = this.authService.getSessionVersion();
    const cachedDirectory = this.configService.getAceFundDirectory();
    if (this.configService.hasAceFundDirectory()) {
      if (this.aceFundDirectoryRef !== cachedDirectory) {
        const directory$ = this.createFundCacheEntry(contextVersion, () =>
          of(cachedDirectory as AceFundDetailDto[])
        );
        this.aceFundDirectoryRef = cachedDirectory;
        return directory$;
      }
      if (this.aceFundsCache?.contextVersion === contextVersion) {
        return this.aceFundsCache.observable;
      }
      const directory$ = this.createFundCacheEntry(contextVersion, () =>
        of(cachedDirectory as AceFundDetailDto[])
      );
      this.aceFundDirectoryRef = cachedDirectory;
      return directory$;
    }

    if (this.aceFundDirectoryRef !== undefined) {
      this.aceFundsCache = undefined;
      this.aceFundDirectoryRef = undefined;
    }

    if (
      this.aceFundsCache &&
      this.aceFundsCache.contextVersion === contextVersion
    ) {
      return this.aceFundsCache.observable;
    }

    const page = (cursor?: string): Observable<AceFundListData | undefined> =>
      this.apiHttpService
        .get(this.apiEndpointsService.getFunds(cursor))
        .pipe(
          map(response =>
            this.unwrapAceResponse<AceFundListData | undefined>(response)
          )
        );
    return this.createFundCacheEntry(contextVersion, () =>
      page().pipe(
        expand(data => (data?.nextCursor ? page(data.nextCursor) : EMPTY)),
        reduce(
          (acc, data) => acc.concat(data?.items ?? []),
          [] as AceFundDetailDto[]
        )
      )
    );
  }

  private createFundCacheEntry(
    contextVersion: number,
    sourceFactory: () => Observable<AceFundDetailDto[]>
  ): Observable<AceFundDetailDto[]> {
    const cacheEntry = {
      contextVersion,
      observable: undefined as unknown as Observable<AceFundDetailDto[]>,
    };
    const request$ = defer(() =>
      this.authService.getSessionVersion() === contextVersion
        ? sourceFactory()
        : EMPTY
    ).pipe(
      takeUntil(this.authService.sessionChanges$),
      catchError(error => {
        if (this.aceFundsCache === cacheEntry) {
          this.aceFundsCache = undefined;
        }
        return this.raise(error);
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
      filter(() => this.authService.getSessionVersion() === contextVersion)
    );
    cacheEntry.observable = request$;
    this.aceFundsCache = cacheEntry;
    return request$;
  }

  private getAllAceTasks(
    fundId?: string,
    filters: TaskBackendFilters = {}
  ): Observable<AceTaskDto[]> {
    const page = (cursor?: string): Observable<AceTaskListData | undefined> =>
      this.apiHttpService
        .get(this.apiEndpointsService.getTasks({ ...filters, fundId, cursor }))
        .pipe(
          map(response =>
            this.unwrapAceResponse<AceTaskListData | undefined>(response)
          )
        );

    return page().pipe(
      expand(data => (data?.nextCursor ? page(data.nextCursor) : EMPTY)),
      reduce((acc, data) => acc.concat(data?.items ?? []), [] as AceTaskDto[])
    );
  }

  private toLegacyTasksResponse(
    tasks: AceTaskDto[],
    scopedFund?: AceFundDetailDto
  ) {
    const legacyTasks = tasks.map(task => this.toLegacyTask(task));
    const topics = this.toTopics(tasks, scopedFund);
    return {
      data: {
        tasks: legacyTasks,
        topics,
        suggestTaskAvailable: true,
      },
    };
  }

  private toLegacyTaskDetailResponse(task?: AceTaskDto) {
    return {
      data: {
        task: task
          ? {
              title: task.title,
              description: this.toReadableDescription(task.description),
              status: this.toLegacyStatus(task.status),
              priority: (task.priority ?? 'MEDIUM').toLowerCase(),
              dueAt: task.dueAt ?? null,
              dueLabel: task.dueLabel ?? null,
              source: task.source ?? 'native',
              externalSource: task.externalSource ?? null,
              externalId: task.externalId ?? null,
              externalSyncStatus: task.externalSyncStatus ?? null,
              externalSyncError: task.externalSyncError ?? null,
              externalUrl: task.externalUrl ?? null,
              clickUp: task.clickUp ?? null,
              company: task.company,
              assignees:
                task.assignees?.map(assignee => this.toTeamMember(assignee)) ??
                [],
              folderId: task.folderId ?? null,
              notifyInApp: task.notifyInApp ?? false,
              notifyEmail: task.notifyEmail ?? false,
              createdAt: task.createdAt ?? null,
              updatedAt: task.updatedAt ?? null,
              completedAt: task.completedAt ?? null,
              documents: task.documents ?? [],
              documentsCount:
                task.documentsCount ?? task.documents?.length ?? 0,
              comments:
                task.comments?.map(comment => ({
                  ...comment,
                  body: comment.body || comment.commentText || '',
                  author: comment.author
                    ? this.toTeamMember(comment.author)
                    : null,
                })) ?? [],
              commentsCount: task.commentsCount ?? task.comments?.length ?? 0,
              checklistItems: task.checklistItems ?? [],
              activity:
                task.activity?.map(event => ({
                  ...event,
                  user: event.user ? this.toTeamMember(event.user) : null,
                })) ?? [],
            }
          : null,
      },
    };
  }

  private toLegacyTask(task: AceTaskDto): LegacyTaskDto {
    const assignees =
      task.assignees?.map(assignee => this.toTeamMember(assignee)) ?? [];
    return {
      id: task.id,
      name: task.title,
      description: this.toReadableDescription(task.description),
      status: this.toLegacyStatus(task.status),
      source: task.source ?? 'native',
      dueAt: task.dueAt ?? null,
      dueLabel: task.dueLabel ?? null,
      topicId: this.topicId(task),
      priority: (task.priority ?? 'MEDIUM').toLowerCase(),
      documentsCount: task.documentsCount ?? 0,
      commentsCount: task.commentsCount ?? 0,
      assignee: assignees[0] ?? null,
      assignees,
    };
  }

  private toTaskListItem(taskId: string, detail: ITaskDetailData): ITaskApi {
    const assignees = detail.assignees ?? [];
    return {
      id: taskId,
      name: detail.title,
      description: this.toReadableDescription(detail.description),
      status: detail.status,
      source: detail.source ?? 'native',
      dueAt: detail.dueAt ?? null,
      dueLabel: detail.dueLabel ?? null,
      topicId: detail.company?.id ?? '',
      priority: (detail.priority ?? 'MEDIUM').toLowerCase(),
      documentsCount: detail.documentsCount ?? 0,
      commentsCount: detail.commentsCount ?? 0,
      assignee: assignees[0] ?? null,
      assignees,
    };
  }

  private filterTasksForCalendar(
    tasks: AceTaskDto[],
    filters: TaskBackendFilters
  ): AceTaskDto[] {
    const statuses = filters.status?.map(status => this.toLegacyStatus(status));
    const dueFrom = filters.dueFrom
      ? new Date(filters.dueFrom).getTime()
      : null;
    const dueTo = filters.dueTo ? new Date(filters.dueTo).getTime() : null;
    const now = Date.now();

    return tasks.filter(task => {
      if (!task.dueAt) {
        return false;
      }

      const dueAt = new Date(task.dueAt).getTime();
      if (Number.isNaN(dueAt)) {
        return false;
      }
      if (
        statuses?.length &&
        !statuses.includes(this.toLegacyStatus(task.status))
      ) {
        return false;
      }
      if (dueFrom !== null && dueAt < dueFrom) {
        return false;
      }
      if (dueTo !== null && dueAt > dueTo) {
        return false;
      }
      if (filters.includePastDue === false && dueAt < now) {
        return false;
      }
      return true;
    });
  }

  private toReadableDescription(description?: string | null): string {
    const value = description?.trim() ?? '';
    if (!value) {
      return '';
    }
    if (value.startsWith('{"success":false') || value.startsWith('{"error":')) {
      return '';
    }
    return value;
  }

  private toTopics(
    tasks: AceTaskDto[],
    scopedFund?: AceFundDetailDto
  ): ITopicListItem[] {
    const byId = new Map<string, ITopicListItem>();
    if (scopedFund) {
      byId.set(scopedFund.id, {
        id: scopedFund.id,
        name: scopedFund.name ?? scopedFund.code ?? 'Fund',
        uncompletedTaskCount: 0,
      });
    }
    for (const task of tasks) {
      const id = this.topicId(task);
      const current = byId.get(id) ?? {
        id,
        name: task.company?.name ?? 'General',
        uncompletedTaskCount: 0,
      };
      if (this.toLegacyStatus(task.status) !== 'Completed') {
        current.uncompletedTaskCount += 1;
      }
      byId.set(id, current);
    }
    return Array.from(byId.values());
  }

  private topicId(task: AceTaskDto): string {
    return task.company?.id ?? 'general';
  }

  private findAceFundForVehicle(
    vehicleId: string,
    funds: AceFundDetailDto[],
    gpId?: string,
    allowLegacyNameMatch = true
  ): AceFundDetailDto | undefined {
    const mappedMatches = funds.filter(fund => fund.vehicleId === vehicleId);
    const mappedFundIds = new Set(mappedMatches.map(fund => fund.id));
    if (mappedFundIds.size === 1) {
      return mappedMatches[0];
    }
    if (mappedFundIds.size > 1) {
      return undefined;
    }

    const directMatch = funds.find(fund => fund.id === vehicleId);
    if (directMatch) {
      return directMatch;
    }

    const vehicle = this.findVehicle(vehicleId, gpId);
    const explicitFundId = vehicle?.aceFundId ?? vehicle?.fundId;
    if (explicitFundId) {
      return funds.find(fund => fund.id === explicitFundId);
    }
    if (!allowLegacyNameMatch) {
      return undefined;
    }
    const vehicleKeys = [
      vehicle?.name,
      vehicle?.code,
      vehicle?.shortName,
      vehicle?.displayName,
    ]
      .map(value => this.normalizeMatchKey(value))
      .filter(Boolean);

    if (!vehicleKeys.length) {
      return undefined;
    }

    const matches = funds.filter(fund => {
      const fundKeys = [fund.name, fund.code].map(value =>
        this.normalizeMatchKey(value)
      );
      return fundKeys.some(key => key && vehicleKeys.includes(key));
    });
    return matches.length === 1 ? matches[0] : undefined;
  }

  private findVehicle(
    vehicleId: string,
    gpId?: string
  ): BaseDataVehicle | undefined {
    const baseData = this.configService.baseData as
      | {
          data?: { gps?: BaseDataGp[] };
          gps?: BaseDataGp[];
        }
      | undefined;
    const gps = baseData?.data?.gps ?? baseData?.gps ?? [];
    for (const gp of gps) {
      if (gpId && gp.id !== gpId) {
        continue;
      }
      const vehicle = gp.vehicles?.find(item => item.id === vehicleId);
      if (vehicle) {
        return vehicle;
      }
    }
    return undefined;
  }

  private normalizeMatchKey(value: unknown): string {
    return typeof value === 'string'
      ? value.toLowerCase().replace(/[^a-z0-9]/g, '')
      : '';
  }

  private asAceFundId(vehicleId: string | undefined): string | undefined {
    return vehicleId &&
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
        vehicleId
      )
      ? vehicleId
      : undefined;
  }

  private toCreateTaskPayload(
    vehicleId: string | undefined,
    data: FormData,
    resolvedFundId?: string
  ): CreateAceTaskPayload {
    const description = this.formValue(data, 'description');
    const dueAt = this.formValue(data, 'dueAt');
    const priority = this.formValue(data, 'priority').toUpperCase();
    const fundId = this.formValue(data, 'fundId');
    const folderId = this.formValue(data, 'folderId');
    const source = this.formValue(data, 'source').toLowerCase();
    const assigneeIds = this.formArrayValue(data, 'assigneeIds');
    const participantIds = this.formArrayValue(data, 'participantIds');
    const checklistItems = this.formArrayValue(data, 'checklistItems')
      .map(item => item.trim())
      .filter(Boolean)
      .map(title => ({ title, completed: false }));

    const payload: CreateAceTaskPayload = {
      source: source === 'clickup' ? 'clickup' : 'native',
      title: this.formValue(data, 'title'),
      description: description || undefined,
      priority: this.isPriority(priority) ? priority : 'MEDIUM',
      dueAt: dueAt ? new Date(dueAt).toISOString() : undefined,
      notifyInApp: this.formBooleanValue(data, 'notifyInApp', true),
      notifyEmail: this.formBooleanValue(data, 'notifyEmail', false),
    };

    if (this.asAceFundId(fundId)) {
      payload.fundId = fundId;
    } else if (resolvedFundId) {
      payload.fundId = resolvedFundId;
    } else if (this.asAceFundId(vehicleId)) {
      payload.fundId = vehicleId;
    }
    if (assigneeIds.length) {
      payload.assigneeIds = assigneeIds.slice(0, 1);
    }
    if (participantIds.length) {
      payload.participantIds = participantIds;
    }
    if (checklistItems.length) {
      payload.checklistItems = checklistItems;
    }
    if (this.asAceFundId(folderId)) {
      payload.folderId = folderId;
    }

    return payload;
  }

  private extractTaskId(response: unknown): string {
    const data = (response as { data?: unknown })?.data ?? response;
    const task = (data as { task?: unknown })?.task ?? data;
    return (task as { id?: string })?.id ?? '';
  }

  private toUpdateTaskPayload(data: FormData): UpdateAceTaskPayload {
    const payload: UpdateAceTaskPayload = {};
    const title = data.has('title') ? this.formValue(data, 'title') : '';
    const description = data.has('description')
      ? this.formValue(data, 'description')
      : '';
    const status = data.has('status')
      ? this.toAceStatus(this.formValue(data, 'status'))
      : undefined;
    const priority = data.has('priority')
      ? this.formValue(data, 'priority').toUpperCase()
      : '';
    const dueAt = data.has('dueAt') ? this.formValue(data, 'dueAt') : '';
    const checklistItems = data.has('checklistItems')
      ? this.formJsonArrayValue<
          NonNullable<UpdateAceTaskPayload['checklistItems']>[number]
        >(data, 'checklistItems')
      : undefined;

    if (title) {
      payload.title = title;
    }
    if (data.has('description')) {
      payload.description = description;
    }
    if (status) {
      payload.status = status;
    }
    if (this.isPriority(priority)) {
      payload.priority = priority;
    }
    if (data.has('dueAt')) {
      payload.dueAt = dueAt ? new Date(dueAt).toISOString() : null;
    }
    if (checklistItems) {
      payload.checklistItems = checklistItems;
    }
    if (data.has('notifyInApp')) {
      payload.notifyInApp = this.formBooleanValue(data, 'notifyInApp', false);
    }
    if (data.has('notifyEmail')) {
      payload.notifyEmail = this.formBooleanValue(data, 'notifyEmail', false);
    }

    return payload;
  }

  private formValue(data: FormData, key: string): string {
    const value = data.get(key);
    return typeof value === 'string' ? value.trim() : '';
  }

  private formArrayValue(data: FormData, key: string): string[] {
    const value = this.formValue(data, key);
    if (!value) return [];
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed.filter(Boolean).map(String) : [];
    } catch {
      return [];
    }
  }

  private formJsonArrayValue<T>(data: FormData, key: string): T[] {
    const value = this.formValue(data, key);
    if (!value) return [];
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  private formBooleanValue(
    data: FormData,
    key: string,
    fallback: boolean
  ): boolean {
    const value = this.formValue(data, key);
    return value ? value === 'true' : fallback;
  }

  private isPriority(value: string): value is 'LOW' | 'MEDIUM' | 'HIGH' {
    return value === 'LOW' || value === 'MEDIUM' || value === 'HIGH';
  }

  private raise(error: unknown): Observable<never> {
    return throwError(() => error);
  }

  private toLegacyStatus(status: string): string {
    const normalized = (status ?? '').toUpperCase().replace(/\s+/g, '_');
    if (normalized === 'DONE' || normalized === 'COMPLETED') {
      return 'Completed';
    }
    if (normalized === 'IN_PROGRESS') {
      return 'In progress';
    }
    if (normalized === 'BLOCKED') {
      return 'Blocked';
    }
    if (normalized === 'IN_REVIEW') {
      return 'In review';
    }
    return 'To do';
  }

  private toAceStatus(
    status: string
  ): UpdateAceTaskPayload['status'] | undefined {
    const normalized = (status ?? '').toLowerCase();
    if (normalized === 'to do' || normalized === 'todo') return 'TODO';
    if (normalized === 'in progress' || normalized === 'in_progress')
      return 'IN_PROGRESS';
    if (normalized === 'blocked') return 'BLOCKED';
    if (normalized === 'in review' || normalized === 'in_review')
      return 'IN_REVIEW';
    if (normalized === 'completed' || normalized === 'done') return 'DONE';
    return undefined;
  }

  private toTeamMember(account: AceTaskAccountDto): TeamMember {
    return {
      id: account.id,
      firstName: account.firstName ?? '',
      lastName: account.lastName ?? '',
      email: account.email,
      roles: (account.roles ?? []).map(role => this.toTeamRole(role)),
      avatarUrl: resolveAvatarUrl(account.profileImageUrl),
      isCurrentUser: false,
    };
  }

  private toTeamRole(role: string): TeamRole {
    const normalized = role.toUpperCase();
    if (normalized === 'ADMIN') return TeamRole.Admin;
    if (normalized === 'OPS') return TeamRole.Ops;
    if (normalized === 'GP') return TeamRole.Gp;
    if (normalized === 'LP') return TeamRole.Lp;
    if (normalized === 'SUPPORTER') return TeamRole.Supporter;
    return TeamRole.Unknown;
  }
}
